/**
 * components/EmptyState/NumberedSteps
 * The three numbered steps beneath a flow strip, laid out in a hairline grid at
 * `minmax(200px, 1fr)`. Each cell: the step number in 10.5px mono grey, the
 * step in 12.5/500, and a hint in 11.5px grey.
 */
import { StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { mono, sans, size as fontSize, leading } from '../../theme/typography';
import HairlineGrid from '../Grid/HairlineGrid';

export interface Step {
  /** "1", "2", "3" — rendered as given, so a caller can label them "01". */
  n: string;
  label: string;
  hint: string;
}

export interface NumberedStepsProps {
  steps: readonly Step[];
}

export function NumberedSteps({ steps }: NumberedStepsProps) {
  return (
    <HairlineGrid min={200}>
      {steps.map((step) => (
        <View key={step.n} style={styles.cell}>
          <Text style={styles.number}>{step.n}</Text>
          <Text style={styles.label}>{step.label}</Text>
          <Text style={styles.hint}>{step.hint}</Text>
        </View>
      ))}
    </HairlineGrid>
  );
}

const styles = StyleSheet.create({
  cell: {
    backgroundColor: neutral.surface,
    paddingVertical: 17,
    paddingHorizontal: 20,
    gap: 2,
  },
  number: { ...mono('400'), fontSize: fontSize.tableHeader, color: neutral.grey400, marginBottom: 4 },
  label: { ...sans('500'), fontSize: fontSize.button, color: neutral.ink },
  hint: {
    ...sans('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: neutral.grey600,
  },
});

export default NumberedSteps;
